import { supabase } from './supabase';

// Browser port of app/src/lib/location.ts. Uses navigator.geolocation instead
// of expo-location. Foreground-only — matches the native stance after the
// 2026-04-20 hardening pass that stripped Background-Location.

export type LocationPermission = 'granted' | 'denied' | 'unavailable';

let watchId: number | null = null;
let lastUpsertAt = 0;
const UPSERT_THROTTLE_MS = 8000;

// Browser permission state is polled lazily — the Permissions API isn't
// available everywhere. We surface granted/denied based on the actual call.
export async function requestLocationPermission(): Promise<LocationPermission> {
  if (!('geolocation' in navigator)) return 'unavailable';
  return new Promise<LocationPermission>((resolve) => {
    navigator.geolocation.getCurrentPosition(
      () => resolve('granted'),
      (err) => {
        if (err.code === err.PERMISSION_DENIED) resolve('denied');
        else resolve('granted'); // position unavailable / timeout — permission itself is fine
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 }
    );
  });
}

export function startSharingLocation(userId: string, onError?: (e: unknown) => void) {
  if (watchId !== null) return;
  if (!('geolocation' in navigator)) return;

  // Prime once for a fast first pin, then a throttled watch for updates.
  navigator.geolocation.getCurrentPosition(
    (pos) => upsert(userId, pos).catch((e) => onError?.(e)),
    (e) => onError?.(e),
    { enableHighAccuracy: false, timeout: 10000, maximumAge: 0 }
  );

  watchId = navigator.geolocation.watchPosition(
    (pos) => {
      upsert(userId, pos).catch((e) => onError?.(e));
    },
    (e) => onError?.(e),
    {
      enableHighAccuracy: false,
      // Browsers throttle updates themselves; we additionally throttle upsert
      // frequency server-side to match native's ~8s cadence.
      maximumAge: 4000,
      timeout: 20000,
    }
  );
}

export function stopSharingLocation() {
  if (watchId !== null) {
    navigator.geolocation.clearWatch(watchId);
    watchId = null;
  }
}

async function upsert(userId: string, pos: GeolocationPosition) {
  const now = Date.now();
  if (now - lastUpsertAt < UPSERT_THROTTLE_MS) return;
  lastUpsertAt = now;
  const { error } = await supabase.from('user_locations').upsert(
    {
      user_id: userId,
      lat: pos.coords.latitude,
      lng: pos.coords.longitude,
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'user_id' }
  );
  if (error) throw error;
}

export async function setPresenceStatus(
  userId: string,
  status: 'online' | 'offline'
): Promise<void> {
  const { error } = await supabase
    .from('user_locations')
    .update({ status: status === 'online' ? null : 'offline' })
    .eq('user_id', userId);
  if (error) throw error;
}
